import { LocalVideoTrack } from 'livekit-client';
import { BackgroundProcessorWrapper } from '../types';
import { IBackgroundProcessorConfigProvider } from '../types/backgroundProcessor';
import { BackgroundProcessorFactory } from './BackgroundProcessorFactory';
import { TrackProcessorManager } from './TrackProcessorManager';
import { StoreBasedConfigProvider } from './StoreBasedConfigProvider';
export class VirtualBackgroundService {
    private managers: Map<string, TrackProcessorManager> = new Map();
    private configProvider: IBackgroundProcessorConfigProvider;
    private processorFactory: BackgroundProcessorFactory;

    public constructor(configProvider: IBackgroundProcessorConfigProvider) {
        this.configProvider = configProvider;
        this.processorFactory = new BackgroundProcessorFactory(configProvider);
    }

    /**
     * Получает или создаёт менеджер по ID
     */
    public getOrCreateManager(id: string): TrackProcessorManager {
        if (!this.managers.has(id)) {
            const manager = new TrackProcessorManager(this.configProvider, this.processorFactory);
            this.managers.set(id, manager);
        }
        return this.managers.get(id)!;
    }

    /**
     * Применяет процессор к треку для указанного менеджера
     */
    public async applyToTrack(id: string, track: LocalVideoTrack | null): Promise<void> {
        const manager = this.getOrCreateManager(id);
        await manager.applyToTrack(track);
    }

    /**
     * Обновляет все менеджеры (при смене фона)
     */
    public async updateAllManagers(): Promise<void> {
        const promises = Array.from(this.managers.entries()).map(async ([id, manager]) => {
            try {
                const track = manager.getCurrentTrack();
                if (track) {
                    await manager.applyToTrack(track);
                } else {
                    console.log('No track to update');
                }
            } catch (error) {
                console.error(`Error updating manager ${id}:`, error);
            }
        });

        await Promise.allSettled(promises);
    }

    /**
     * Удаляет менеджер
     */
    public async removeManager(id: string, destroy: boolean = true): Promise<void> {
        const manager = this.managers.get(id);
        if (!manager) return;

        if (destroy) {
            try {
                await manager.removeProcessor();
                manager.destroy();
            } catch (error) {
                console.warn(`Error destroying manager ${id}:`, error);
            }
        }

        this.managers.delete(id);
    }

    /**
     * Удаляет все менеджеры
     */
    public async removeAllManagers(destroy: boolean = true): Promise<void> {
        await Promise.allSettled(Array.from(this.managers.keys()).map((id) => this.removeManager(id, destroy)));
    }

    /**
     * Уничтожает все менеджеры и освобождает ресурсы
     */
    public destroy(): void {
        this.managers.forEach((manager) => {
            try {
                manager.destroy();
            } catch (error) {
                console.warn('Error destroying manager:', error);
            }
        });
        this.managers.clear();
    }

    /**
     * Возвращает процессор для указанного менеджера
     */
    public getProcessor(id: string): BackgroundProcessorWrapper | null {
        const manager = this.managers.get(id);
        return manager?.getProcessor() || null;
    }
}

const configProvider = new StoreBasedConfigProvider();
export const virtualBackgroundService = new VirtualBackgroundService(configProvider);
